import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  AlertSeverityEnum,
  // diasPermitidosInterface,
  UseFetchEnabledParams,
  UseMutationsParams,
  UsuariosSistemaGET,
  UsuariosSistemaGETPagingRes,
} from '@/shared/interfaces';
import { getUrlParamsFetch, handleAxiosError } from '@/shared/utils';
import { useUiStore } from '@/store/ui';

const { get, post, put } = erpApi();

export enum UsuarioSistemaQueryKeys {
  USUARIOS_SISTEMA = 'usuarios-sistema',
}
//CONTROL-CAMBIOS
export const useFetchUsuariosSistemaPorArreglo = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<GetUsuariosSistemaParams>) => {
  return useQuery({
    queryKey: ["usuarios-por-arreglo", params],
    queryFn: async () => {
      return getUsuariosSistema_porArreglo(params);
    },
    enabled: enabled,
  });
};

export const getUsuariosSistema_porArreglo = (params?: GetUsuariosSistemaParams) => {
  const queryParams = new URLSearchParams();

  if (params?.usuarios?.length) {
    queryParams.append("usuarios", params.usuarios.join(","));
  }
  if (params?.page !== undefined && params?.page !== null) {
    queryParams.append("page", params.page.toString());
  }

  if (params?.page_size !== undefined && params?.page_size !== null) {
    queryParams.append("page_size", params.page_size.toString());
  }
   if (params?.paginate !== undefined && params?.paginate !== null) {   // ← nuevo
    queryParams.append("paginate", params.paginate.toString());
  }

  return get<UsuariosSistemaGETPagingRes>(
    `${urlBackend}api/v1/get-usuarios-por-arreglo/?${queryParams.toString()}`,
    true
  );
};


//
export const useFetchUsuariosSistemaSinModulos = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<GetUsuariosSistemaParams>) => {
  return useQuery({
    queryKey: ["usuarios-sin-modulos", params],
    queryFn: async () => {
      return getUsuariosSistemaSinModulos(params);
    },
    enabled: enabled,
  });
};
export const getUsuariosSistemaSinModulos = (params?: GetUsuariosSistemaParams) => {
  const queryParams = getUrlParamsFetch(params ?? {});

  return get<UsuariosSistemaGETPagingRes>(
    `${urlBackend}api/v1/get-usuario-control-cambios/?${queryParams}`,
    true
  );
};


///* tanStack query
export const useFetchUsuariosSistema = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<GetUsuariosSistemaParams>) => {
  return useQuery({
    queryKey: [UsuarioSistemaQueryKeys.USUARIOS_SISTEMA, params],
    queryFn: async () => {
      return getUsuariosSistema(params);
    },
    enabled: enabled,
  });
};

export const useGetUsuarioSistema = (id: number) => {
  return useQuery({
    queryKey: [UsuarioSistemaQueryKeys.USUARIOS_SISTEMA, id],
    queryFn: () => getUserSistema(id),
    retry: false,
  });
};

export const useCreateUsuarioSistema = <T>({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: (data: CreateUsuariosSistemaParams<T>) => {
      return createUsuariosSistema(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [UsuarioSistemaQueryKeys.USUARIOS_SISTEMA],
      });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Usuario creado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateUsuarioSistema = <T>({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: (params: UpdateUsuariosSistemaParams<T>) => {
      return updateUsuariosSistema(params);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [UsuarioSistemaQueryKeys.USUARIOS_SISTEMA],
      });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Usuario actualizado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

///* axios
export type GetUsuariosSistemaParams = Partial<UsuariosSistemaGET> & {
  page?: number;
  page_size?: number;
  usuarios?: number[];
  razon_social?:string;
  paginate?:boolean;
};
export type CreateUsuariosSistemaParams<T> = T;
export type UpdateUsuariosSistemaParams<T> = {
  id: number;
  data: T;
};

export const getUsuariosSistema = (params?: GetUsuariosSistemaParams) => {
  const queryParams = getUrlParamsFetch(params ?? {});

  return get<UsuariosSistemaGETPagingRes>(
    `${urlBackend}api/v1/get-usuarios/?${queryParams}`,
    true
  );
};

export const getUserSistema = async (id: number) => {
  try {
    return await get<UsuariosSistemaGET>(
      `${urlBackend}api/v1/user/${id}/`,
      true
    );
  } catch (error) {
    handleAxiosError(error);
  }
};

export const createUsuariosSistema = <T>(
  data: CreateUsuariosSistemaParams<T>
) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<UsuariosSistemaGET>(
    `${urlBackend}api/v1/crear-usuario/`,
    data,
    true
  );
};

export const updateUsuariosSistema = async <T>(
  params: UpdateUsuariosSistemaParams<T>
) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<UsuariosSistemaGET>(
    `${urlBackend}api/v1/user/${params.id}/`,
    params.data,
    true
  );
};

///* types ==============
export type CreateUpdUserSistemaData = UsuariosSistemaGET | {
  tipo_identificacion: string; // choice
  identificacion: string;
  razon_social: string; // en ambos

  username: string;
  password: string;
  email: string;

  ///* fk
  empresa: string;
  area: string;
  departamento: string;
  rol: string;
  canal_venta?: string; // code

  grupos: number[];
  permisos: number[];
  // dias_permitidos?: diasPermitidosInterface[] | [] | null;
};
